/**
 * Cookie utility functions for handling browser cookies safely
 */

/**
 * Get a cookie value by name, handling edge cases properly
 * @param name - The name of the cookie to retrieve
 * @returns The cookie value or undefined if not found
 */
export const getCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();

    // Find the position of the first '=' in the cookie
    const eqIndex = cookie.indexOf('=');

    // If no '=' found, skip this cookie
    if (eqIndex === -1) {
      continue;
    }

    const cookieName = cookie.substring(0, eqIndex);

    if (cookieName === name) {
      // Return everything after the first '=' as the value
      return cookie.substring(eqIndex + 1);
    }
  }

  return undefined;
};

/**
 * Set a cookie with proper encoding and options
 * @param name - The name of the cookie
 * @param value - The value to set
 * @param options - Optional cookie settings (expires, path, domain, etc.)
 */
export const setCookie = (
  name: string,
  value: string,
  options?: {
    expires?: Date;
    path?: string;
    domain?: string;
    secure?: boolean;
    sameSite?: 'strict' | 'lax' | 'none';
  }
): void => {
  if (typeof document === 'undefined') {
    return;
  }

  let cookieString = `${name}=${encodeURIComponent(value)}`;

  if (options) {
    if (options.expires) {
      cookieString += `; expires=${options.expires.toUTCString()}`;
    }

    if (options.path) {
      cookieString += `; path=${options.path}`;
    }

    if (options.domain) {
      cookieString += `; domain=${options.domain}`;
    }

    if (options.secure) {
      cookieString += '; secure';
    }

    if (options.sameSite) {
      cookieString += `; samesite=${options.sameSite}`;
    }
  }

  document.cookie = cookieString;
};

/**
 * Delete a cookie by setting its expiration to the past
 * @param name - The name of the cookie to delete
 * @param options - Optional cookie settings (path, domain)
 */
export const deleteCookie = (
  name: string,
  options?: {
    path?: string;
    domain?: string;
  }
): void => {
  setCookie(name, '', {
    expires: new Date(0),
    path: options?.path,
    domain: options?.domain
  });
};
